import { useCallback, useEffect, useState } from 'react';
import { BrandMark } from '@cwos/ui-admin';
import { get, post } from '../../lib/api.js';

/**
 * Organization chooser (pre-session).
 *
 * When an identity holds more than one ACTIVE membership the OIDC callback
 * deliberately mints no session: it sets a short-lived signed org-selection
 * cookie and redirects here. Until this screen existed, that redirect landed on
 * the app root with no session and the user was bounced straight back to
 * sign-in — i.e. anyone belonging to two organizations could not log in at all.
 *
 * Security shape — this screen holds no authority of its own:
 *   - the membership list comes from GET /auth/pending-orgs, which resolves it
 *     SERVER-SIDE from the httpOnly signed cookie's verified subject;
 *   - choosing an organization calls POST /auth/select-org, which independently
 *     re-checks that the subject has an ACTIVE membership in the chosen org
 *     before minting the session. A tampered or replayed orgId is rejected 403;
 *   - on success we do a full page load rather than a client-side transition, so
 *     the app boots from a fresh /auth/me against the new tenant-scoped session.
 */

interface PendingMembership {
  orgId: string;
  orgName: string;
  orgSlug: string | null;
  roles: string[];
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; memberships: PendingMembership[] }
  | { kind: 'expired' }
  | { kind: 'failed'; message: string };

export function SelectOrg() {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [pendingOrgId, setPendingOrgId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    get<{ memberships: PendingMembership[] }>('/auth/pending-orgs')
      .then((result) => {
        if (cancelled) return;
        setPhase({ kind: 'ready', memberships: result.memberships });
      })
      .catch((err: Error & { status?: number }) => {
        if (cancelled) return;
        // 401 means the selection window closed (or the cookie is absent /
        // tampered). Signing in again is the only correct recovery.
        setPhase(
          err.status === 401
            ? { kind: 'expired' }
            : { kind: 'failed', message: 'Could not load your organizations.' },
        );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const choose = useCallback(async (orgId: string) => {
    setPendingOrgId(orgId);
    setError(null);
    try {
      await post('/auth/select-org', { orgId });
      // Full page load: the session cookie is new, and every cached query in the
      // app belongs to the previous (absent) tenant context.
      window.location.assign('/');
    } catch (err) {
      const status = (err as Error & { status?: number }).status;
      setPendingOrgId(null);
      setError(
        status === 403
          ? 'You are not an active member of that organization.'
          : status === 401
            ? 'Your selection window expired. Sign in again.'
            : 'Could not open that organization. Please try again.',
      );
    }
  }, []);

  return (
    <main className="signin">
      <section className="signin-brand" aria-label="Phibian">
        <div className="signin-brand-inner">
          <div className="signin-logo">
            <BrandMark variant="onWater" size={39} />
            <span className="signin-wordmark">phibian</span>
          </div>
          <div className="signin-hero">
            <div className="signin-eyebrow">Operations without boundaries</div>
            <h1 className="signin-hero-h1">Choose an organization.</h1>
            <p className="signin-hero-p">
              Your account has access to more than one organization. Pick the one you want to work
              in — you can switch at any time from the account menu.
            </p>
          </div>
          <div className="signin-brand-foot">
            <span>
              <i aria-hidden="true" /> Workforce
            </span>
            <span>
              <i aria-hidden="true" /> Field operations
            </span>
            <span>
              <i aria-hidden="true" /> Operational intelligence
            </span>
          </div>
        </div>
      </section>

      <section className="signin-panel">
        <div className="signin-wrap">
          <div className="signin-card">
            <h2 className="signin-h2">Select your organization</h2>
            <p className="signin-sub">
              You are signed in. Choose where to continue — access is checked again when you do.
            </p>

            {phase.kind === 'loading' ? (
              <p className="rn-muted" aria-live="polite">
                Loading your organizations…
              </p>
            ) : null}

            {phase.kind === 'expired' ? (
              <>
                <p className="signin-err" role="alert">
                  Your selection window expired. Please sign in again.
                </p>
                <a className="signin-primary" href="/auth/login/start">
                  Back to sign in
                </a>
              </>
            ) : null}

            {phase.kind === 'failed' ? (
              <>
                <p className="signin-err" role="alert">
                  {phase.message}
                </p>
                <a className="signin-primary" href="/auth/login/start">
                  Back to sign in
                </a>
              </>
            ) : null}

            {phase.kind === 'ready' ? (
              phase.memberships.length === 0 ? (
                <>
                  <p className="signin-err" role="alert">
                    No active organization is available for your account. Contact your
                    administrator.
                  </p>
                  <a className="signin-primary" href="/auth/login/start">
                    Back to sign in
                  </a>
                </>
              ) : (
                <ul className="org-choices">
                  {phase.memberships.map((m) => (
                    <li key={m.orgId}>
                      <button
                        type="button"
                        className="org-choice"
                        disabled={pendingOrgId !== null}
                        aria-busy={pendingOrgId === m.orgId}
                        onClick={() => void choose(m.orgId)}
                      >
                        <span className="org-choice-avatar" aria-hidden="true">
                          {initials(m.orgName)}
                        </span>
                        <span className="org-choice-copy">
                          <span className="org-choice-name">{m.orgName}</span>
                          {m.roles.length > 0 ? (
                            <span className="org-choice-roles">
                              {m.roles.map(humanRole).join(' · ')}
                            </span>
                          ) : null}
                        </span>
                        <span className="org-choice-go" aria-hidden="true">
                          {pendingOrgId === m.orgId ? '…' : '→'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )
            ) : null}

            {error ? (
              <p className="signin-err" role="alert">
                {error}
              </p>
            ) : null}
          </div>
        </div>
      </section>
    </main>
  );
}

/** `org_admin` reads as "Org_admin" under CSS capitalization; show "Org admin". */
function humanRole(role: string): string {
  return role.replace(/_/g, ' ');
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const first = parts[0] as string;
  if (parts.length === 1) return first.slice(0, 2).toUpperCase();
  const last = parts[parts.length - 1] as string;
  return (first.charAt(0) + last.charAt(0)).toUpperCase();
}
